import React, { useEffect, useMemo, useState } from "react";
import { CalendarCheck, Loader2, Plus, TriangleAlert, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";

const addDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);
export const shift = (date, n) =>
  new Date(new Date(`${date}T00:00:00`).getTime() + n * 864e5).toISOString().slice(0, 10);
export const spanDays = (a, b) =>
  Math.max(1, Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 864e5) + 1);
/** Travel mirrors the session dates for on-site visits only, until someone edits it by hand. */
export const withTravel = (f, edited) => {
  if (edited) return f;
  const start = f.proposed_start ?? f.start_date;
  const end = (f.proposed_end ?? f.end_date) || start;
  return f.mode === "Online" || f.mode === "At our office"
    ? { ...f, travel_start: "", travel_end: "" }
    : { ...f, travel_start: start || "", travel_end: end || "" };
};

const blank = {
  kind: "Demo", customer_id: "", lead_id: "", contact_source: "customer", contact_ref: "",
  customer_name: "", company_name: "", contact_person: "",
  contact_email: "", contact_phone: "", mode: "On-site", location: "",
  proposed_start: addDays(7), proposed_end: addDays(7), days: 1,
  travel_start: "", travel_end: "", agenda: "", notify_customer: true,
};

export const DemoForm = ({ open, onOpenChange, demo, onSaved }) => {
  const editing = !!demo;
  const [form, setForm] = useState(blank);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [picked, setPicked] = useState([]);
  const [team, setTeam] = useState([]);
  const [staff, setStaff] = useState([]);
  const [travelEdited, setTravelEdited] = useState(false);
  const [clashes, setClashes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? { ...blank, ...demo } : withTravel(blank, false));
    setTravelEdited(editing && !!demo.travel_start
      && (demo.travel_start !== demo.proposed_start || demo.travel_end !== (demo.proposed_end || demo.proposed_start)));
    setClashes([]);
    setPicked(editing ? (demo.products || []) : []);
    setTeam(editing ? (demo.assigned_to_ids || []) : []);
    api.get("/demos/contacts").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/staff/assignable").then((r) => setStaff(r.data)).catch(() => {});
  }, [open, demo, editing]);

  useEffect(() => {
    if (!open || !team.length || !form.proposed_start) return setClashes([]);
    const t = setTimeout(() => {
      api.get("/demos/clashes", {
        params: {
          start: form.travel_start || form.proposed_start,
          end: form.travel_end || form.proposed_end || form.proposed_start,
          staff: team.join(","), exclude: demo?.id || "",
        },
      }).then((r) => setClashes(r.data)).catch(() => setClashes([]));
    }, 400);
    return () => clearTimeout(t);
  }, [open, team, form.proposed_start, form.proposed_end, form.travel_start, form.travel_end, demo?.id]);

  const setStart = (v) => setForm((f) => {
    const end = f.proposed_end && f.proposed_end >= v ? f.proposed_end : v;
    return withTravel({ ...f, proposed_start: v, proposed_end: end, days: spanDays(v, end) }, travelEdited);
  });
  const setEnd = (v) => setForm((f) => withTravel({
    ...f, proposed_end: v,
    days: v && f.proposed_start && v >= f.proposed_start ? spanDays(f.proposed_start, v) : f.days,
  }, travelEdited));
  const setDays = (v) => setForm((f) => {
    const n = Number(v);
    const end = n >= 1 && f.proposed_start ? shift(f.proposed_start, n - 1) : f.proposed_end;
    return withTravel({ ...f, days: v, proposed_end: end }, travelEdited);
  });
  const setMode = (v) => {
    const nonTravel = v === "Online" || v === "At our office";
    if (nonTravel) setTravelEdited(false);
    setForm((f) => withTravel({ ...f, mode: v }, travelEdited && !nonTravel));
  };

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    if (!c) return;
    const isLead = c.kind === "lead";
    setForm((f) => ({
      ...f,
      customer_id: isLead ? "" : c.id, lead_id: isLead ? c.id : "",
      contact_source: c.kind, contact_ref: c.ref || "",
      customer_name: c.customer_name, company_name: c.company || "",
      contact_person: f.contact_person || c.customer_name,
      contact_email: c.email || f.contact_email, contact_phone: c.phone || f.contact_phone,
      location: f.location || [c.city, c.state].filter(Boolean).join(", "),
    }));
  };

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const remaining = useMemo(
    () => products.filter((p) => !picked.some((x) => x.product_id === p.id)), [products, picked]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name) return toast.error("Choose the customer this is for.");
    if (clashes.length) {
      toast.warning(`Saved anyway - ${clashes[0].people.join(", ")} is also booked on ${clashes[0].number}.`);
    }
    setBusy(true);
    const payload = {
      ...form, days: Number(form.days || 1), products: picked, assigned_to_ids: team,
      proposed_end: form.proposed_end || form.proposed_start,
    };
    try {
      if (editing) await api.patch(`/demos/${demo.id}`, payload);
      else {
        const { data } = await api.post("/demos", payload);
        toast.success(`${form.kind} ${data.number} created`);
        if (data.email?.status === "outbox") {
          toast.info("The customer email is waiting in the Email Outbox until SMTP is configured.");
        } else if (data.email?.status === "sent") {
          toast.success(`Proposed dates emailed to ${form.contact_email}`);
        }
      }
      if (editing) toast.success("Updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto scroll-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {editing ? `Edit ${demo.number}` : "Schedule a demo or training"}
          </DialogTitle>
          <DialogDescription>
            Propose the dates to the customer. Once they agree, confirm them and the customer gets a
            confirmation email automatically.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="This is a" required>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="df-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Demo">Demo</SelectItem>
                  <SelectItem value="Training">Training</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Customer or CRM lead" required
              hint="Existing customers and open leads are both listed. A completed visit is stamped back onto the CRM record.">
              <Select value={form.lead_id || form.customer_id} onValueChange={pickCustomer}>
                <SelectTrigger data-testid="df-customer">
                  <SelectValue placeholder={form.customer_name || "Select a customer or lead"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Customers</SelectLabel>
                    {customers.filter((c) => c.kind === "customer").map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.customer_name}{c.company ? ` \u00b7 ${c.company}` : ""}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>CRM leads</SelectLabel>
                    {customers.filter((c) => c.kind === "lead").map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.customer_name}{c.company ? ` \u00b7 ${c.company}` : ""} &middot; {c.status}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Contact person">
              <Input value={form.contact_person} data-testid="df-contact"
                onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
            </Field>
            <Field label="Contact email" hint="All scheduling emails go here.">
              <Input type="email" value={form.contact_email} data-testid="df-email"
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </Field>
            <Field label="Contact phone">
              <Input value={form.contact_phone} data-testid="df-phone"
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            </Field>
            <Field label="Mode">
              <Select value={form.mode} onValueChange={setMode}>
                <SelectTrigger data-testid="df-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["On-site", "Online", "At our office"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Venue / location">
            <Input value={form.location} data-testid="df-location"
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Customer plant, Sanand, Ahmedabad" />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Proposed start" required>
              <Input type="date" required value={form.proposed_start} data-testid="df-start"
                onChange={(e) => setStart(e.target.value)} />
            </Field>
            <Field label="Proposed end">
              <Input type="date" value={form.proposed_end} data-testid="df-end"
                min={form.proposed_start} onChange={(e) => setEnd(e.target.value)} />
            </Field>
            <Field label="Duration (days)" hint="Kept in step with the dates.">
              <Input type="number" min="0.5" step="0.5" value={form.days} data-testid="df-days"
                onChange={(e) => setDays(e.target.value)} />
            </Field>
            <Field label="Travel starts"
              hint={form.mode === "On-site" ? "Follows the visit dates until you change it." : "Not needed for this mode."}>
              <Input type="date" value={form.travel_start} data-testid="df-travel-start"
                onChange={(e) => { setTravelEdited(true); setForm({ ...form, travel_start: e.target.value }); }} />
            </Field>
            <Field label="Travel ends">
              <Input type="date" value={form.travel_end} data-testid="df-travel-end"
                onChange={(e) => { setTravelEdited(true); setForm({ ...form, travel_end: e.target.value }); }} />
            </Field>
            {travelEdited && (
              <div className="flex items-end">
                <Button type="button" size="sm" variant="ghost" data-testid="df-travel-reset"
                  onClick={() => { setTravelEdited(false); setForm((f) => withTravel(f, false)); }}>
                  Match the visit dates again
                </Button>
              </div>
            )}
          </div>

          {clashes.length > 0 && (
            <div className="rounded-xl border border-warning/40 bg-warning-soft/50 p-3" data-testid="df-clash-warning">
              <p className="flex items-center gap-2 text-sm font-semibold text-warning">
                <TriangleAlert className="h-4 w-4" /> Someone is already booked on these dates
              </p>
              <ul className="mt-1.5 space-y-1 text-xs text-muted-foreground">
                {clashes.map((c) => (
                  <li key={c.id} className="num">
                    {c.people.join(", ")} &middot; {c.number} {c.customer_name} &middot; {c.start} to {c.end} ({c.status})
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-xs text-muted-foreground">You can still save this if it is intentional.</p>
            </div>
          )}

          <Field label="Products to be demonstrated">
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {picked.map((p) => (
                  <span key={p.product_id} className="flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary-soft px-2.5 py-1 text-xs font-medium">
                    {p.name}
                    <button type="button" data-testid={`df-product-remove-${p.name}`} onClick={() => setPicked(picked.filter((x) => x.product_id !== p.product_id))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {picked.length === 0 && <span className="text-xs text-muted-foreground">None selected yet.</span>}
              </div>
              <Select value="" onValueChange={(id) => {
                const p = products.find((x) => x.id === id);
                if (p) setPicked([...picked, { product_id: p.id, name: p.name, sku: p.sku }]);
              }}>
                <SelectTrigger data-testid="df-products"><SelectValue placeholder="Add a product" /></SelectTrigger>
                <SelectContent>
                  {remaining.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Field>

          <Field label="Team members attending" hint="They are notified and their productivity is tracked.">
            <div className="grid gap-2 sm:grid-cols-2">
              {staff.map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                  <Checkbox checked={team.includes(s.id)} data-testid={`df-team-${s.id}`}
                    onCheckedChange={() => toggle(team, setTeam, s.id)} />
                  {s.name}
                </label>
              ))}
            </div>
          </Field>

          <Field label="Agenda">
            <Textarea rows={2} value={form.agenda} data-testid="df-agenda"
              onChange={(e) => setForm({ ...form, agenda: e.target.value })}
              placeholder="Machine setup, operator training, quality checks" />
          </Field>

          {!editing && (
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-primary/30 bg-primary-soft/40 px-3 py-2.5 text-sm font-medium">
              <Checkbox checked={form.notify_customer} data-testid="df-notify"
                onCheckedChange={(v) => setForm({ ...form, notify_customer: !!v })} />
              Email the proposed dates to the customer now
            </label>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="df-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="df-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? <CalendarCheck className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {editing ? "Save changes" : `Schedule ${form.kind.toLowerCase()}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
